import { useState, useMemo, useEffect, useRef } from "react";
import { goBack } from "@/lib/goBack";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Calendar as CalIcon, Download,
  CheckCircle2, DollarSign, Wallet, FileCheck, Loader2, Flag, FileText, HardHat,
  Paperclip, Send, Lock, Truck, Upload, Pencil,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { AuthImg, openTimelineFile } from "@/components/AuthFile";
import { POChat } from "@/pages/Orders";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, fmtCurrency, fmtCurrencyExact } from "@/lib/constants";
import { JobDocButtons } from "@/components/JobBomHandover";
import JobBudgetDialog from "@/components/JobBudgetDialog";
import JobInstallPlan from "@/components/JobInstallPlan";

const WORKFLOW_META = {
  deposit_received: { icon: Wallet, desc: "Ticks automatically when the deposit is recorded via the green Deposit received button" },
  scheduled: { icon: CalIcon, desc: "Ticks automatically once the schedule dates are populated from the Project planner's tasks" },
  materials_ordered: { icon: Truck, desc: "Ticks automatically once a linked purchase order has been sent to the supplier" },
  in_progress: { icon: HardHat, desc: "Ticks automatically when materials are received — any linked PO marked delivered" },
  completed: { icon: DollarSign, desc: "Ticks automatically when the job is marked completed" },
};

export default function JobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);

  const { data: job } = useQuery({ queryKey: ["job", id], queryFn: async () => (await api.get(`/jobs/${id}`)).data });
  const { data: companies = [] } = useQuery({ queryKey: ["companies-all-jd"], queryFn: async () => (await api.get("/companies")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: planData } = useQuery({ queryKey: ["project-plan", id], queryFn: async () => (await api.get(`/project/plan?include_job=${id}`)).data });
  const planTaskCount = planData?.jobs?.find((j) => j.id === id)?.tasks?.length || 0;
  const { data: wf } = useQuery({ queryKey: ["job-workflow", id], queryFn: async () => (await api.get(`/jobs/${id}/workflow`)).data, refetchInterval: 60000 });

  const updateJob = useMutation({
    mutationFn: async (patch) => (await api.patch(`/jobs/${id}`, patch)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["job", id] }); qc.invalidateQueries({ queryKey: ["job-workflow", id] }); qc.invalidateQueries({ queryKey: ["jobs"] }); qc.invalidateQueries({ queryKey: ["profit-summary"] }); toast.success("Saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed — your changes were NOT saved"),
  });
  const workflowSteps = useMemo(() => (wf?.steps || []).map((s) => ({ ...s, ...(WORKFLOW_META[s.key] || {}) })), [wf]);
  const doneSteps = workflowSteps.filter((s) => s.done).length;
  const progressPct = wf?.pct ?? 0;

  if (!job) return <div className="p-10 text-slate-500">Loading…</div>;
  const company = companies.find((c) => c.id === job.company_id);

  const manDayRate = job.man_day_rate || (settings?.labour_rate || 85) * 8;
  const revenue = job.quote_sale_price || job.invoice_amount || 0;
  const labourBudget = job.labour_budget ?? (job.budgeted_man_days || 0) * manDayRate;
  const labourBudgetSource = job.labour_budget_source;
  const budgetedMaterials = job.budgeted_materials_cost || 0;
  const actualMaterials = job.actual_materials_cost || 0;
  const actualLabour = job.actual_labour_cost != null
    ? job.actual_labour_cost
    : (job.actual_man_days || 0) * manDayRate;
  const budgetedCost = labourBudget + budgetedMaterials;
  const actualCost = actualLabour + actualMaterials;
  const variance = actualCost - budgetedCost;
  const grossProfit = revenue - actualCost;
  const grossMarginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;

  return (
    <div>
      <PageHeader
        title={job.title}
        subtitle={
          <>
            {job.job_number || "No JN"} ·{" "}
            {company ? (
              <Link to={`/clients/${company.id}`} className="text-cyan-700 font-semibold hover:underline" data-testid="job-client-link">
                {company.name}
              </Link>
            ) : "—"}{" "}
            · {progressPct}% complete
          </>
        }
        breadcrumb={["Operations", "Jobs", job.job_number || job.title]}
        actions={
          <>
            <Button variant="outline" onClick={() => goBack(nav, "/jobs")} className="rounded-sm h-10"><ArrowLeft size={16} className="mr-1" /> Back</Button>
            {job.deal_id && (
              <Button
                data-testid="view-original-deal-btn"
                variant="outline"
                onClick={() => nav(`/deals?open=${job.deal_id}`)}
                className="rounded-sm h-10 border-violet-300 text-violet-700 hover:bg-violet-50"
                title="Open the original enquiry / quote / deal this install came from"
              >
                <FileText size={14} className="mr-1.5" /> View Deal
              </Button>
            )}
            {job.status === "completed" ? (
              <Button data-testid="reopen-job-btn" variant="outline" onClick={() => updateJob.mutate({ status: "in_progress" })}
                disabled={updateJob.isPending} className="rounded-sm h-10 border-amber-300 text-amber-700 hover:bg-amber-50">
                {updateJob.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Flag size={14} className="mr-1.5" />} Re-open
              </Button>
            ) : job.status !== "cancelled" && (
              <Button data-testid="mark-completed-btn" onClick={() => setCompleteOpen(true)}
                className="rounded-sm h-10 bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 size={16} className="mr-1.5" /> Mark Completed
              </Button>
            )}
            <JobDocButtons jobId={id} />
          </>
        }
      />

      <div className="px-6 lg:px-10 py-6 grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-5 min-w-0">
          {/* Progress bar */}
          <Card className="rounded-sm border-slate-200 industrial-shadow p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="stamp-label text-slate-500">Overall Progress</div>
                <div className="font-display text-3xl font-bold text-slate-900 mt-1">
                  {progressPct}<span className="text-xl text-slate-400">%</span>
                </div>
              </div>
              <div className="text-right">
                <div className="stamp-label text-slate-500">Steps</div>
                <div className="font-display text-xl font-semibold mt-1">
                  {doneSteps} / {workflowSteps.length || 5}
                </div>
              </div>
            </div>
            <div className="w-full h-3 bg-slate-100 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-600 transition-all duration-500" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="grid grid-cols-5 gap-0.5 mt-1">
              {workflowSteps.map((s) => (
                <div key={s.key} className={`h-1 ${s.done ? "bg-cyan-600" : "bg-slate-200"}`} title={s.label} />
              ))}
            </div>
          </Card>

          {/* Workflow — 5 auto-tracked milestone steps */}
          <Card className="rounded-sm border-slate-200 industrial-shadow">
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="stamp-label text-slate-500">Workflow</div>
              <h3 className="font-display text-lg font-semibold mt-0.5">Install workflow</h3>
              <p className="text-xs text-slate-500 mt-0.5">Steps tick automatically — nothing to edit here</p>
            </div>
            <div className="divide-y divide-slate-100">
              {workflowSteps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.key} className={`flex items-start gap-3 p-4 ${s.done ? "bg-emerald-50/30" : ""}`} data-testid={`workflow-step-${s.key}`}>
                    <div className={`w-9 h-9 flex items-center justify-center shrink-0 font-mono font-bold text-xs ${s.done ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Icon size={14} className={s.done ? "text-emerald-600" : "text-slate-400"} />
                        <span className={`font-medium ${s.done ? "text-slate-600" : "text-slate-900"}`}>{s.label}</span>
                        <Badge variant="outline" className="rounded-sm text-[9px] border-slate-300 text-slate-500">AUTO</Badge>
                        {s.done && s.when && <span className="text-[10px] text-emerald-700 font-mono">{fmtDate(s.when)}</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                    </div>
                    {s.done && <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-2" data-testid={`workflow-done-${s.key}`} />}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Project plan — live from the Scheduler's Project planner */}
          <JobInstallPlan job={job} />

          {/* Budget vs Actual */}
          <Card className="rounded-sm border-slate-200 industrial-shadow">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
              <div>
                <div className="stamp-label text-slate-500">Cost Analysis</div>
                <h3 className="font-display text-lg font-semibold mt-0.5">Budget vs Actual</h3>
              </div>
              <Button data-testid="budget-costs-btn" size="sm" onClick={() => setBudgetOpen(true)} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white shrink-0">
                <Wallet size={14} className="mr-1.5" /> Budget &amp; Costs
              </Button>
            </div>
            <div className="p-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <BudgetCard label="Revenue" value={fmtCurrency(revenue)} sub={job.quote_sale_price ? "Accepted quote amount" : "Invoice amount"} accent="text-slate-900" />
              <BudgetCard label="Budgeted Materials" value={fmtCurrency(budgetedMaterials)} sub="Set in Budget & Costs" />
              <BudgetCard label="Actual Materials" value={fmtCurrency(actualMaterials)} sub="Ledger — POs + manual entries" accent={budgetedMaterials > 0 && actualMaterials > budgetedMaterials ? "text-rose-600" : "text-slate-900"} />
              <BudgetCard label="Budgeted Labour" value={fmtCurrency(labourBudget)} sub={labourBudgetSource === "manual" ? "Set in Budget & Costs" : labourBudgetSource === "planner" ? "From planner crew billing" : "Legacy man-days × rate"} />
              <BudgetCard label="Actual Labour" value={fmtCurrency(actualLabour)} sub="Ledger — incl. AUTO crew costs" accent={labourBudget > 0 && actualLabour > labourBudget ? "text-rose-600" : "text-slate-900"} />
              <BudgetCard label="Total Budget Variance" value={`${variance > 0 ? "+" : ""}${fmtCurrency(Math.abs(variance))}`} sub={variance > 0 ? `Over budget (${fmtCurrency(budgetedCost)} total)` : variance < 0 ? `Under budget (${fmtCurrency(budgetedCost)} total)` : "On budget"} accent={variance > 0 ? "text-rose-600" : "text-emerald-600"} />
              <BudgetCard label="Gross Profit" value={fmtCurrency(grossProfit)} sub="Revenue − actual costs" accent={grossProfit >= 0 ? "text-emerald-600" : "text-rose-600"} />
              <BudgetCard label="Gross Margin" value={`${grossMarginPct.toFixed(1)}%`} sub={`Actual costs ${fmtCurrency(actualCost)}`} accent={grossMarginPct >= 0 ? "text-slate-900" : "text-rose-600"} />
              <BudgetCard label="Material Lead Time"
                value={job.material_lead_days != null ? `${job.material_lead_days} day${job.material_lead_days === 1 ? "" : "s"}` : "—"}
                sub={job.material_lead_days == null
                  ? "Set in Budget & Costs"
                  : job.scheduled_start
                    ? `Order by ${fmtDate(new Date(new Date(job.scheduled_start.slice(0, 10)).getTime() - job.material_lead_days * 86400000).toISOString())}`
                    : <span className="text-slate-300">Order by — (set a job start date)</span>}
                accent={job.material_lead_days != null && job.scheduled_start
                  && new Date(new Date(job.scheduled_start.slice(0, 10)).getTime() - job.material_lead_days * 86400000) < new Date(new Date().toDateString())
                  ? "text-rose-600" : "text-slate-900"} />
            </div>
            <div className="px-5 pb-4 text-[11px] text-slate-500" data-testid="man-day-rate-note">
              {labourBudgetSource === "planner"
                ? <>Labour budget from the <b>Project planner's crew billing</b> — crew costs post to the expense ledger automatically (AUTO entries in Budget &amp; Costs) and move with the schedule.</>
                : labourBudgetSource === "manual"
                  ? <>Labour budget set as a dollar value in <b>Budget &amp; Costs</b> — actual labour tracks against it from the expense ledger.</>
                  : <>Man-day rate: <b>{fmtCurrencyExact(manDayRate)}</b> · {job.man_day_rate_source || "Default rate"}</>}
            </div>
          </Card>

          {/* Deal chat + attachments split (Trent, Nov 2026) */}
          <JobCollabPanel job={job} />
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden">
            <div className="bg-slate-900 text-white p-5">
              <div className="flex items-center justify-between">
                <div className="stamp-label text-cyan-400">Schedule</div>
                <span className="text-[9px] font-semibold uppercase tracking-widest bg-cyan-500/15 text-cyan-300 border border-cyan-500/40 rounded-sm px-1.5 py-0.5" data-testid="schedule-from-planner-tag">From planner</span>
              </div>
              {job.scheduled_start ? (
                <div className="font-display text-base font-semibold mt-1.5" data-testid="schedule-dates">{fmtDate(job.scheduled_start)} → {fmtDate(job.scheduled_end)}</div>
              ) : (
                <div className="text-sm font-medium text-slate-300 mt-1.5" data-testid="schedule-no-plan">No install plan yet</div>
              )}
              <div className="text-[11px] text-slate-400 mt-1" data-testid="schedule-source-note">
                {planTaskCount > 0
                  ? <>Driven by {planTaskCount} planner task{planTaskCount === 1 ? "" : "s"} — edit the install plan to move the dates.</>
                  : <>Add tasks to the install plan on this page to set the dates.</>}
              </div>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <SiteAddressRow job={job} />
              <WhsRow job={job} />
              <QuoteRefRow job={job} />
            </div>
          </Card>

          <Card className="rounded-sm border-slate-200 industrial-shadow p-5">
            <div className="stamp-label text-slate-500 mb-2">Financials</div>
            <FinancialsPanel job={job} />
            {!job.deposit_received_at && <DepositReceivedButton job={job} />}
            <XeroInvoicePanel job={job} />
          </Card>

          {/* POs linked to this job (Trent, Nov 2026) */}
          <JobPOsCard jobId={job.id} />
        </aside>
      </div>

      {/* Mark completed confirm */}
      <JobBudgetDialog job={job} open={budgetOpen} onClose={() => setBudgetOpen(false)} />
      <Dialog open={completeOpen} onOpenChange={(o) => !o && setCompleteOpen(false)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="mark-completed-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-500" /> Mark this job as completed?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            {progressPct < 100 && (
              <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800">
                <b>Heads up — only {progressPct}% of workflow steps are complete.</b> You can still mark it completed — the Invoiced step will tick automatically.
              </div>
            )}
            <p><b>{job.title}</b> will move out of Open jobs into <b>Completed</b>. Any linked deal and downstream automations for <code>job_completed</code> will fire. You can re-open the job from this same page if needed.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="confirm-mark-completed-btn"
              onClick={() => { updateJob.mutate({ status: "completed" }); setCompleteOpen(false); }}
              disabled={updateJob.isPending}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
              {updateJob.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
              Yes, mark completed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FinancialsPanel({ job }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const quoteTotal = job.quote_sale_price || 0;
  const deposit = job.deposit_amount || 0;
  const finalInvoice = quoteTotal > 0 ? Math.round((quoteTotal - deposit) * 100) / 100 : (job.invoice_amount || 0);
  const pct = quoteTotal > 0 && deposit > 0 ? Math.round((deposit / quoteTotal) * 100) : null;
  const half = Math.round(quoteTotal * 50) / 100;
  const draftDeposit = parseFloat(amount) || 0;
  const save = useMutation({
    mutationFn: async () => (await api.patch(`/jobs/${job.id}/financials`, { deposit_amount: draftDeposit })).data,
    onSuccess: () => { setOpen(false); toast.success("Deposit updated — final invoice adjusted"); qc.invalidateQueries({ queryKey: ["job", job.id] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update deposit"),
  });
  return (
    <div className="space-y-2 text-sm" data-testid="job-financials">
      <Row2 label="Quote total" value={fmtCurrencyExact(quoteTotal)}
        sub={quoteTotal > 0 ? "Accepted quote amount" : "No accepted quote linked"} />
      <div className="flex items-center justify-between">
        <div>
          <div className="stamp-label text-slate-500 text-[10px]">Deposit{pct != null ? ` (${pct}%)` : ""}</div>
          <div className="text-xs text-slate-500">{job.deposit_received_at ? `Received ${fmtDate(job.deposit_received_at)}` : "Pending"}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="font-display text-lg font-bold text-slate-900" data-testid="financials-deposit">{fmtCurrencyExact(deposit)}</div>
          {quoteTotal > 0 && (
            <button data-testid="edit-deposit-btn" title="Change deposit amount"
              onClick={() => { setAmount(String(deposit || half)); setOpen(true); }}
              className="text-slate-400 hover:text-cyan-600"><Pencil size={13} /></button>
          )}
        </div>
      </div>
      <Row2 label="Final invoice" value={fmtCurrencyExact(finalInvoice)}
        sub={job.invoice_paid_at ? `Paid ${fmtDate(job.invoice_paid_at)}` : job.invoice_sent_at ? `Sent ${fmtDate(job.invoice_sent_at)}` : "Quote total minus deposit"} />
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="edit-deposit-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Deposit — {job.job_number || job.title}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Quote total</span><b className="font-mono text-slate-900">{fmtCurrencyExact(quoteTotal)}</b>
            </div>
            <div>
              <div className="stamp-label text-slate-500">Deposit amount $</div>
              <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="edit-deposit-input" className="mt-1 rounded-sm" />
              <button onClick={() => setAmount(String(half))} data-testid="deposit-5050-btn"
                className="text-[11px] text-cyan-700 hover:underline mt-1">Reset to 50/50 ({fmtCurrencyExact(half)})</button>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200 rounded-sm">
              <span className="text-xs text-slate-500">Final invoice (quote total − deposit)</span>
              <b className="font-mono text-slate-900" data-testid="edit-deposit-final-preview">{fmtCurrencyExact(Math.max(quoteTotal - draftDeposit, 0))}</b>
            </div>
            {draftDeposit > quoteTotal && <div className="text-xs text-rose-600" data-testid="deposit-too-big">Deposit can't exceed the quote total.</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button disabled={save.isPending || draftDeposit > quoteTotal} onClick={() => save.mutate()}
              className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="save-deposit-btn">
              {save.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row2({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="stamp-label text-slate-500 text-[10px]">{label}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </div>
      <div className="font-display text-lg font-bold text-slate-900">{value}</div>
    </div>
  );
}

function BudgetCard({ label, value, sub, accent = "text-slate-900" }) {
  return (
    <div className="border border-slate-200 p-3">
      <div className="stamp-label text-slate-500">{label}</div>
      <div className={`font-display text-xl font-bold mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function XeroInvoicePanel({ job }) {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["xero-status"], queryFn: async () => (await api.get("/xero/status")).data });
  const { data: invoices = [] } = useQuery({
    queryKey: ["xero-job-invoices", job.id],
    queryFn: async () => (await api.get(`/xero/jobs/${job.id}/invoices`)).data,
    enabled: !!status?.connected,
  });
  const create = useMutation({
    mutationFn: async (invoice_type) => (await api.post(`/xero/jobs/${job.id}/invoice`, { invoice_type })).data,
    onSuccess: (d) => {
      toast.success(`Xero invoice ${d.invoice_number || ""} created — ${d.invoice_type}`);
      qc.invalidateQueries({ queryKey: ["xero-job-invoices", job.id] });
      qc.invalidateQueries({ queryKey: ["job", job.id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create Xero invoice"),
  });
  if (!status?.connected) {
    return (
      <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-400">
        Connect Xero in System → Integrations to invoice this job directly.
      </div>
    );
  }
  const dep = invoices.find((i) => i.invoice_type === "deposit" && i.status !== "VOIDED");
  const fin = invoices.find((i) => i.invoice_type === "final" && i.status !== "VOIDED");
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2" data-testid="xero-invoice-panel">
      <div className="stamp-label text-emerald-700">Xero — {status.org_name}</div>
      {invoices.map((i) => (
        <div key={i.id} className="flex items-center justify-between text-xs" data-testid={`xero-invoice-${i.invoice_type}`}>
          <span className="font-mono text-slate-700">{i.invoice_number} · {i.invoice_type}</span>
          <Badge variant="outline" className={`rounded-sm text-[9px] ${i.status === "PAID" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
            {i.status}{i.amount_paid ? ` · $${i.amount_paid.toLocaleString("en-AU")}` : ""}
          </Badge>
        </div>
      ))}
      <div className="grid grid-cols-2 gap-2">
        {!dep && (
          <Button size="sm" variant="outline" disabled={create.isPending} onClick={() => create.mutate("deposit")}
            className="rounded-sm h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs" data-testid="xero-invoice-deposit-btn">
            {create.isPending ? <Loader2 size={12} className="animate-spin mr-1" /> : <DollarSign size={12} className="mr-1" />} Invoice deposit
          </Button>
        )}
        {!fin && (
          <Button size="sm" variant="outline" disabled={create.isPending} onClick={() => create.mutate("final")}
            className="rounded-sm h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs" data-testid="xero-invoice-final-btn">
            {create.isPending ? <Loader2 size={12} className="animate-spin mr-1" /> : <DollarSign size={12} className="mr-1" />} Invoice balance
          </Button>
        )}
      </div>
    </div>
  );
}

function SiteAddressRow({ job }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [pickOptions, setPickOptions] = useState(null);
  const [pulling, setPulling] = useState(false);
  const save = useMutation({
    mutationFn: async (site_address) => (await api.patch(`/jobs/${job.id}`, { site_address })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", job.id] });
      setEditing(false); setPickOptions(null);
      toast.success("Site address saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save site address"),
  });
  const pullFromClient = async () => {
    setPulling(true);
    try {
      const { data } = await api.get(`/jobs/${job.id}/client-addresses`);
      const opts = data.options || [];
      if (opts.length === 0) toast.info("No address found on the client profile");
      else if (opts.length === 1) save.mutate(opts[0].address);
      else setPickOptions(opts);
    } catch {
      toast.error("Could not load the client's addresses");
    }
    setPulling(false);
  };
  return (
    <div data-testid="site-address-row">
      <div className="flex items-center justify-between">
        <div className="stamp-label text-slate-500 flex items-center gap-1.5"><MapPin size={13} /> Site address</div>
        {!editing && (
          <button type="button" className="text-[11px] text-cyan-700 font-semibold hover:underline" data-testid="edit-site-address-btn"
            onClick={() => { setVal(job.site_address || ""); setEditing(true); }}>
            {job.site_address ? "Edit" : "Add"}
          </button>
        )}
      </div>
      {!editing ? (
        <div className="text-sm text-slate-800 mt-1" data-testid="site-address-value">{job.site_address || <span className="text-slate-400">—</span>}</div>
      ) : (
        <div className="mt-1.5 space-y-1.5">
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="e.g. 14 Beachside Ave, Terrigal NSW 2260"
            className="h-9 rounded-sm text-sm" data-testid="site-address-input" autoFocus
            onKeyDown={(e) => e.key === "Enter" && save.mutate(val.trim())} />
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button size="sm" className="rounded-sm h-7 text-xs bg-cyan-600 hover:bg-cyan-700 text-white" disabled={save.isPending}
              onClick={() => save.mutate(val.trim())} data-testid="save-site-address-btn">Save</Button>
            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs"
              onClick={() => { setEditing(false); setPickOptions(null); }} data-testid="cancel-site-address-btn">Cancel</Button>
            <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs border-cyan-200 text-cyan-700 hover:bg-cyan-50" disabled={pulling || save.isPending}
              onClick={pullFromClient} data-testid="use-client-address-btn">
              {pulling ? <Loader2 size={11} className="animate-spin mr-1" /> : <Download size={11} className="mr-1" />} Use client address
            </Button>
          </div>
          {pickOptions && (
            <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 bg-slate-50" data-testid="client-address-picker">
              <div className="px-2.5 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Pick a client address</div>
              {pickOptions.map((o, i) => (
                <button key={i} type="button" className="w-full text-left px-2.5 py-2 text-xs hover:bg-cyan-50"
                  onClick={() => save.mutate(o.address)} data-testid={`client-address-opt-${i}`}>
                  <span className="font-semibold text-slate-700">{o.label}</span> — {o.address}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WhsRow({ job }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const save = useMutation({
    mutationFn: async (whs_plan_url) => (await api.patch(`/jobs/${job.id}`, { whs_plan_url })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["job", job.id] }); setEditing(false); toast.success("WHS plan link saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });
  return (
    <div data-testid="whs-row">
      <div className="flex items-center justify-between">
        <div className="stamp-label text-slate-500 flex items-center gap-1.5"><FileCheck size={13} /> WHS plan</div>
        {!editing && (
          <button type="button" className="text-[11px] text-cyan-700 font-semibold hover:underline" data-testid="edit-whs-btn"
            onClick={() => { setVal(job.whs_plan_url || ""); setEditing(true); }}>
            {job.whs_plan_url ? "Edit" : "Add"}
          </button>
        )}
      </div>
      {!editing ? (
        <div className="text-sm mt-1">
          {job.whs_plan_url
            ? <a href={job.whs_plan_url} target="_blank" rel="noreferrer" className="text-cyan-600 hover:underline truncate inline-block max-w-[240px]" data-testid="whs-link">View plan</a>
            : <span className="text-slate-400">—</span>}
        </div>
      ) : (
        <div className="mt-1.5 flex items-center gap-1.5">
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Link to PDF" className="h-8 rounded-sm text-xs"
            data-testid="whs-input" onKeyDown={(e) => e.key === "Enter" && save.mutate(val.trim())} autoFocus />
          <Button size="sm" className="rounded-sm h-7 text-xs bg-cyan-600 hover:bg-cyan-700 text-white" disabled={save.isPending}
            onClick={() => save.mutate(val.trim())} data-testid="save-whs-btn">Save</Button>
          <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => setEditing(false)} data-testid="cancel-whs-btn">Cancel</Button>
        </div>
      )}
    </div>
  );
}

function QuoteRefRow({ job }) {
  const nav = useNavigate();
  const [relinkOpen, setRelinkOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["job-quote-ref", job.id], queryFn: async () => (await api.get(`/jobs/${job.id}/quote-ref`)).data });
  const q = data?.quote;
  if (!q) return null;
  return (
    <div className="-mx-5 -mb-5 px-5 py-4 bg-cyan-50/60 border-t border-cyan-100" data-testid="job-quote-ref">
      <div className="flex items-center justify-between">
        <div className="stamp-label text-cyan-700 flex items-center gap-1.5"><FileText size={13} /> Accepted quote</div>
        {q.status === "accepted" && <Badge variant="outline" className="rounded-sm text-[9px] border-emerald-300 bg-emerald-50 text-emerald-800">ACCEPTED</Badge>}
      </div>
      <div className="flex items-baseline gap-3 mt-1.5">
        <button type="button" className="font-mono text-sm font-bold text-cyan-700 underline underline-offset-2 hover:text-cyan-900"
          onClick={() => nav(`/quotes/${q.id}`)} data-testid="job-quote-ref-number">{q.quote_number}</button>
        {q.sale_price != null && <span className="font-mono text-sm font-semibold text-slate-800">{fmtCurrencyExact(q.sale_price)}</span>}
      </div>
      <div className="flex items-center justify-between mt-1">
        <button type="button" className="text-[11px] font-bold text-cyan-700 hover:underline"
          onClick={() => nav(`/quotes/${q.id}`)} data-testid="job-quote-ref-open">Open quote — scope of works →</button>
        <button type="button" className="text-[11px] font-semibold text-slate-500 hover:text-cyan-700 hover:underline"
          onClick={() => setRelinkOpen(true)} data-testid="job-relink-quote-btn"
          title="Point this job at a different accepted quote — e.g. when the client accepted a newer quote">
          Relink…
        </button>
      </div>
      {relinkOpen && <RelinkQuoteDialog job={job} current={q} onClose={() => setRelinkOpen(false)} />}
    </div>
  );
}

function RelinkQuoteDialog({ job, current, onClose }) {
  const qc = useQueryClient();
  const [pick, setPick] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["job-relink-options", job.id], queryFn: async () => (await api.get(`/jobs/${job.id}/relink-options`)).data });
  const options = data?.options || [];
  const picked = options.find((o) => o.id === pick);
  const depKept = data?.deposit_received ? (data?.deposit_amount || 0) : null;
  const newDep = picked ? (depKept != null ? depKept : Math.round(picked.sale_price * 50) / 100) : 0;
  const relink = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${job.id}/relink-quote`, { quote_id: pick })).data,
    onSuccess: () => {
      toast.success("Job relinked — financials now follow the new accepted quote");
      qc.invalidateQueries({ queryKey: ["job", job.id] });
      qc.invalidateQueries({ queryKey: ["job-quote-ref", job.id] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not relink quote"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="relink-quote-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Relink accepted quote — {job.job_number || job.title}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs text-slate-500">Choose the accepted quote this job should follow. The current link is <b className="font-mono">{current.quote_number}</b> ({fmtCurrencyExact(current.sale_price || 0)}).</div>
          {isLoading && <div className="py-6 text-center"><Loader2 className="animate-spin inline text-slate-400" /></div>}
          {!isLoading && options.length === 0 && (
            <div className="text-xs text-slate-500 border border-slate-200 rounded-sm p-3" data-testid="relink-no-options">
              No other accepted quotes found for this client — the client needs to accept the new quote (or revision) first.
            </div>
          )}
          {options.map((o) => (
            <button key={o.id} type="button" onClick={() => setPick(o.id)}
              className={`w-full text-left border rounded-sm p-3 transition-colors ${pick === o.id ? "border-2 border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-cyan-300"}`}
              data-testid={`relink-option-${o.quote_number}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-bold">{o.quote_number}{o.revision >= 2 ? ` · Rev ${o.revision}` : ""}</span>
                <span className="font-mono text-sm font-semibold">{fmtCurrencyExact(o.sale_price || 0)}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Accepted {o.accepted_at ? fmtDate(o.accepted_at) : ""}{o.signature_name ? ` · signed by ${o.signature_name}` : ""}
              </div>
              {o.existing_job_number && (
                <div className={`text-[11px] mt-0.5 ${o.existing_job_progressed ? "text-rose-600 font-semibold" : "text-amber-700"}`}>
                  {o.existing_job_progressed
                    ? `⚠ Its job ${o.existing_job_number} already has progress — relink will be blocked until that job is resolved`
                    : `Its auto-created job ${o.existing_job_number} will be cancelled and merged into this one`}
                </div>
              )}
            </button>
          ))}
          {picked && (
            <div className="border border-slate-200 bg-slate-50 rounded-sm p-3 text-xs space-y-1" data-testid="relink-consequences">
              <div className="font-bold text-slate-700">What happens on relink:</div>
              <div>• Job follows <b className="font-mono">{picked.quote_number}</b> — quote total becomes <b className="font-mono">{fmtCurrencyExact(picked.sale_price || 0)}</b></div>
              <div>• {depKept != null
                ? <>Deposit already received (<b className="font-mono">{fmtCurrencyExact(depKept)}</b>) is kept</>
                : <>Deposit resets to 50/50 (<b className="font-mono">{fmtCurrencyExact(newDep)}</b>)</>}</div>
              <div>• Final invoice adjusts to <b className="font-mono">{fmtCurrencyExact(Math.max((picked.sale_price || 0) - newDep, 0))}</b></div>
              <div>• Old quote <b className="font-mono">{current.quote_number}</b> is marked superseded — hidden from the client portal</div>
              <div>• Everything is logged in the audit trail</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button disabled={!pick || relink.isPending} onClick={() => relink.mutate()}
            className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="relink-confirm-btn">
            {relink.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null}
            {picked ? `Relink job to ${picked.quote_number}` : "Relink"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DepositReceivedButton({ job }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("eft");
  const [reference, setReference] = useState("");
  const mark = useMutation({
    mutationFn: async () => (await api.patch(`/jobs/${job.id}/payments/deposit`, {
      amount: parseFloat(amount) || 0, method, reference: reference.trim() || null,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", job.id] });
      qc.invalidateQueries({ queryKey: ["job-workflow", job.id] });
      setOpen(false);
      toast.success("Deposit recorded — job is live, progress tracker started");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not record deposit"),
  });
  return (
    <>
      <Button onClick={() => { setAmount(String(job.deposit_amount || "")); setOpen(true); }}
        className="rounded-sm w-full h-9 mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="deposit-received-btn">
        <Wallet size={14} className="mr-1.5" /> Deposit received
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="deposit-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Record deposit — {job.job_number || job.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Amount received (AUD)">
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="deposit-amount-input" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Method">
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="deposit-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eft">Bank transfer (EFT)</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="stripe">Stripe / online</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Reference (optional)">
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. receipt #" />
              </Field>
            </div>
            <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3 text-xs text-emerald-900">
              Recording the deposit sets the job to <b>In Progress</b> and kicks off the progress tracker (the first stage task is created for the team).
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => {
              if (!(parseFloat(amount) > 0)) return toast.error("Enter the amount received");
              mark.mutate();
            }} disabled={mark.isPending} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-deposit-btn">
              {mark.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Wallet size={14} className="mr-1.5" />} Record deposit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---- Install-job collaboration hub (Trent, Nov 2026) ----
// Split panel: the linked deal's chat (two-way, same thread) + attachments.
function JobCollabPanel({ job }) {
  const qc = useQueryClient();
  const { data: unread } = useQuery({
    queryKey: ["jobs-unread"],
    queryFn: async () => (await api.get("/jobs/unread/summary")).data,
    refetchInterval: 60000,
  });
  // Freeze the read-marker from the FIRST summary so "new since last look"
  // dots survive the auto mark-read below.
  const [frozenSince, setFrozenSince] = useState(null);
  useEffect(() => {
    if (frozenSince === null && unread?.since) setFrozenSince(unread.since[job.id] || "");
  }, [unread, frozenSince, job.id]);
  // Opening the job auto-clears its unread badge.
  const marked = useRef(false);
  useEffect(() => {
    if (!marked.current && unread?.jobs?.[job.id]) {
      marked.current = true;
      api.post(`/jobs/${job.id}/mark-read`).then(() => qc.invalidateQueries({ queryKey: ["jobs-unread"] })).catch(() => {});
    }
  }, [unread, job.id, qc]);
  if (!job.deal_id) {
    return (
      <Card className="rounded-sm border-slate-200 industrial-shadow p-5 text-sm text-slate-400" data-testid="job-collab-nodeal">
        No linked deal — notes &amp; attachments appear here once the job is linked to a deal.
      </Card>
    );
  }
  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="job-collab-panel">
      <JobDealChat dealId={job.deal_id} jobNumber={job.job_number} />
      <JobAttachments dealId={job.deal_id} since={frozenSince || ""} />
    </div>
  );
}

function JobDealChat({ dealId, jobNumber }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [staged, setStaged] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const boxRef = useRef(null);
  const { data } = useQuery({
    queryKey: ["deal-chat", dealId],
    queryFn: async () => (await api.get(`/deals/${dealId}/chat`)).data,
    refetchInterval: 15000,
  });
  const msgs = data?.messages || [];
  useEffect(() => { boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight }); }, [msgs.length]);

  const send = async () => {
    if (sending || (!text.trim() && staged.length === 0)) return;
    setSending(true);
    try {
      const ids = [];
      for (const f of staged) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("deal_id", dealId);
        const r = await api.post("/timeline/files", fd);
        ids.push(r.data.id);
      }
      await api.post(`/deals/${dealId}/notes`, { text: text.trim(), attachment_ids: ids });
      setText(""); setStaged([]);
      qc.invalidateQueries({ queryKey: ["deal-chat", dealId] });
      qc.invalidateQueries({ queryKey: ["timeline-files-deal", dealId] });
      qc.invalidateQueries({ queryKey: ["deals-unread"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the note");
    } finally { setSending(false); }
  };
  const timeOf = (iso) => { try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow flex flex-col" data-testid="job-deal-chat">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2 flex-wrap">
        <div className="stamp-label text-slate-600">Deal Notes &amp; Chat</div>
        <Badge variant="outline" className="rounded-sm text-[9px] border-cyan-300 bg-cyan-50 text-cyan-700 font-bold" data-testid="job-chat-live-badge">
          LIVE · two-way with deal {jobNumber || ""}
        </Badge>
      </div>
      <div ref={boxRef} className="h-72 overflow-y-auto bg-slate-50 p-3 space-y-2">
        {msgs.map((m, idx) => {
          const mine = m.by && m.by === user?.id;
          return (
            <div key={m.id || idx} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 ${mine ? "bg-cyan-700 text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`} data-testid={`job-chat-msg-${m.id}`}>
                {!mine && <div className="text-[10px] font-bold text-cyan-700 mb-0.5">{m.by_name || "—"}</div>}
                {m.text && <div className="text-[13px] whitespace-pre-wrap leading-relaxed">{m.text}</div>}
                {(m.attachments || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {m.attachments.map((a) => (
                      <button key={a.id} type="button" onClick={() => openTimelineFile(a.id)}
                        className={`inline-flex items-center gap-1.5 text-[11px] underline rounded px-2 py-1 max-w-[200px] ${mine ? "bg-white/20 text-white" : "bg-slate-100 text-cyan-700"}`}>
                        <Paperclip size={10} className="shrink-0" /> <span className="truncate">{a.label || a.filename}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className={`text-[9px] font-mono mt-1 text-right ${mine ? "text-cyan-100/80" : "text-slate-400"}`}>{timeOf(m.at)}</div>
              </div>
            </div>
          );
        })}
        {msgs.length === 0 && <div className="text-xs text-slate-400 text-center py-8">No notes on the deal yet — start the thread below.</div>}
      </div>
      {staged.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 pt-2">
          {staged.map((f, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-[11px] bg-cyan-50 border border-cyan-200 text-cyan-800 rounded px-2 py-1">
              <Paperclip size={10} /> {f.name}
              <button type="button" onClick={() => setStaged(staged.filter((_, j) => j !== i))} className="hover:text-rose-600">✕</button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2 p-3 border-t border-slate-200">
        <input ref={fileRef} type="file" multiple className="hidden" data-testid="job-chat-file-input"
          onChange={(e) => { setStaged([...staged, ...Array.from(e.target.files || [])].slice(0, 10)); e.target.value = ""; }} />
        <button type="button" onClick={() => fileRef.current?.click()} title="Attach any file"
          className="w-9 h-9 rounded-full border border-slate-300 text-slate-500 hover:text-cyan-700 hover:border-cyan-400 flex items-center justify-center shrink-0" data-testid="job-chat-attach-btn">
          <Paperclip size={15} />
        </button>
        <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a note — it also appears on the deal…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="rounded-2xl text-sm min-h-[38px] max-h-28 resize-none py-2" data-testid="job-chat-input" />
        <button type="button" onClick={send} disabled={sending || (!text.trim() && staged.length === 0)}
          className="w-10 h-10 rounded-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white flex items-center justify-center shrink-0" data-testid="job-chat-send-btn">
          {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
        </button>
      </div>
    </Card>
  );
}

function JobAttachments({ dealId, since }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const upRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { data: files = [] } = useQuery({
    queryKey: ["timeline-files-deal", dealId],
    queryFn: async () => (await api.get(`/timeline/files?deal_id=${dealId}`)).data,
  });
  const sorted = [...files].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const upload = async (list) => {
    setUploading(true);
    try {
      for (const f of list.slice(0, 10)) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("deal_id", dealId);
        await api.post("/timeline/files", fd);
      }
      qc.invalidateQueries({ queryKey: ["timeline-files-deal", dealId] });
      qc.invalidateQueries({ queryKey: ["deal-chat", dealId] });
      toast.success("Uploaded — visible on the deal too");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow flex flex-col" data-testid="job-attachments-panel">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="stamp-label text-slate-600">Attachments</div>
        <span className="text-[10px] text-slate-400">{sorted.length} file{sorted.length === 1 ? "" : "s"} · deal + job</span>
      </div>
      <div className="p-3 grid grid-cols-3 gap-2 overflow-y-auto max-h-[340px]">
        <input ref={upRef} type="file" multiple className="hidden" data-testid="job-attachments-upload-input"
          onChange={(e) => { upload(Array.from(e.target.files || [])); e.target.value = ""; }} />
        <button type="button" onClick={() => upRef.current?.click()} disabled={uploading}
          className="border-2 border-dashed border-slate-300 rounded-sm h-[92px] flex flex-col items-center justify-center text-slate-400 hover:text-cyan-600 hover:border-cyan-400 text-[10px] gap-1"
          data-testid="job-attachments-upload-btn">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload
        </button>
        {sorted.map((f) => {
          const isImg = (f.content_type || "").startsWith("image/");
          const isNew = since && (f.created_at || "") > since && f.uploaded_by_id !== user?.id;
          return (
            <button key={f.id} type="button" onClick={() => openTimelineFile(f.id)}
              className="relative border border-slate-200 rounded-sm overflow-hidden text-left hover:border-cyan-400 group" data-testid={`job-attachment-${f.id}`}>
              <div className="h-16 bg-slate-100">
                {isImg ? <AuthImg fileId={f.id} alt={f.filename} /> : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400"><FileText size={22} /></div>
                )}
              </div>
              <div className="px-1.5 py-1 text-[10px] truncate text-slate-700">{f.label || f.filename}</div>
              {isNew && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-sky-500 ring-2 ring-white" data-testid={`job-attachment-new-${f.id}`} />}
            </button>
          );
        })}
        {sorted.length === 0 && <div className="col-span-2 flex items-center text-xs text-slate-400">No files yet — photos and documents from the deal land here.</div>}
      </div>
      <div className="px-4 pb-3 text-[10px] text-slate-400">Blue dot = added since you last looked · click any file to open full size.</div>
    </Card>
  );
}

// ---- Linked purchase orders card (right column) ----
const PO_STATUS_META = {
  draft: { label: "DRAFT", cls: "border-slate-300 text-slate-600" },
  pending_approval: { label: "PENDING APPROVAL", cls: "border-amber-300 bg-amber-50 text-amber-800" },
  changes_requested: { label: "CHANGES REQUESTED", cls: "border-rose-300 bg-rose-50 text-rose-700" },
  approved: { label: "APPROVED", cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  ordered: { label: "ORDERED", cls: "border-cyan-300 bg-cyan-50 text-cyan-800" },
  delivered: { label: "DELIVERED", cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
};
const poDelayed = (o) => !!(o?.expected_date && o?.expected_delivery && o.expected_date.slice(0, 10) > o.expected_delivery.slice(0, 10));

function JobPOsCard({ jobId }) {
  const qc = useQueryClient();
  const [view, setView] = useState(null);
  const { data: orders = [] } = useQuery({ queryKey: ["orders"], queryFn: async () => (await api.get("/orders")).data });
  const { data: poUnread } = useQuery({
    queryKey: ["orders-unread"],
    queryFn: async () => (await api.get("/orders/unread/summary")).data,
    refetchInterval: 60000,
  });
  const unreadMap = poUnread?.orders || {};
  const pos = orders.filter((o) => o.job_id === jobId).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const expectedDate = useMutation({
    mutationFn: async ({ id, expected_date }) => (await api.patch(`/orders/${id}/expected-date`, { expected_date })).data,
    onSuccess: (data, vars) => {
      setView((prev) => (prev && prev.id === vars.id ? { ...prev, expected_date: vars.expected_date } : prev));
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["po-chat", vars.id] });
      toast.success("Expected date updated — logged in the PO updates");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update the expected date"),
  });
  const openPO = (o) => {
    setView(o);
    if (unreadMap[o.id]) api.post(`/orders/${o.id}/mark-read`).then(() => qc.invalidateQueries({ queryKey: ["orders-unread"] })).catch(() => {});
  };
  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow" data-testid="job-pos-card">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="stamp-label text-slate-600 flex items-center gap-1.5"><Truck size={12} /> Purchase Orders</div>
        <span className="text-[10px] text-slate-400">{pos.length} linked</span>
      </div>
      <div className="divide-y divide-slate-100">
        {pos.map((o) => {
          const meta = PO_STATUS_META[o.status] || PO_STATUS_META.draft;
          const delayed = poDelayed(o);
          const exp = o.expected_date || o.expected_delivery;
          return (
            <div key={o.id} className={`px-4 py-2.5 cursor-pointer hover:bg-cyan-50/30 ${unreadMap[o.id] ? "bg-sky-50/70" : ""}`}
              onClick={() => openPO(o)} data-testid={`job-po-row-${o.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                {unreadMap[o.id] && <span className="w-2 h-2 rounded-full bg-sky-500 shrink-0" />}
                <span className="font-mono text-xs font-semibold text-cyan-700 underline underline-offset-2">{o.order_number}</span>
                {unreadMap[o.id] && <Badge className="rounded-full text-[9px] bg-amber-400 text-slate-900 border-0 font-bold hover:bg-amber-400" data-testid={`job-po-unread-${o.id}`}>{unreadMap[o.id]} new</Badge>}
                <Badge variant="outline" className={`ml-auto rounded-sm text-[8px] font-bold ${meta.cls}`}>{meta.label}</Badge>
              </div>
              <div className="flex items-center gap-2 mt-1 text-[11px]">
                <span className="text-slate-400">Expected:</span>
                {exp ? (
                  <span className={delayed ? "text-rose-600 font-semibold" : "text-slate-600"}>{fmtDate(exp)}</span>
                ) : <span className="text-slate-400">—</span>}
                {delayed && <Badge className="rounded-sm text-[8px] px-1 bg-rose-600 text-white border-0 font-bold hover:bg-rose-600" data-testid={`job-po-delayed-${o.id}`}>DELAYED</Badge>}
                <span className="ml-auto font-mono text-slate-500">{fmtCurrencyExact(o.total || 0)}</span>
              </div>
            </div>
          );
        })}
        {pos.length === 0 && <div className="px-4 py-5 text-xs text-slate-400">No purchase orders raised for this job yet.</div>}
      </div>
      <div className="px-4 py-2 border-t border-slate-100 text-[10px] text-slate-400">Click a PO to open it here — you stay on this page.</div>

      <Dialog open={!!view} onOpenChange={(v) => { if (!v) setView(null); }}>
        <DialogContent className="rounded-sm sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="job-po-dialog">
          {view && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl flex items-center gap-2 flex-wrap">
                  {view.order_number}
                  <Badge variant="outline" className={`rounded-sm text-[9px] font-bold ${(PO_STATUS_META[view.status] || PO_STATUS_META.draft).cls}`}>{(PO_STATUS_META[view.status] || PO_STATUS_META.draft).label}</Badge>
                  {poDelayed(view) && <Badge className="rounded-sm text-[9px] bg-rose-600 text-white border-0 font-bold hover:bg-rose-600">DELAYED</Badge>}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="border border-slate-200 rounded-sm p-3">
                  <div className="stamp-label text-slate-600 mb-2">Delivery dates</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] stamp-label text-slate-400 flex items-center gap-1"><Lock size={9} /> Requested — locked on send</div>
                      <div className="text-sm font-semibold text-slate-800 mt-1.5" data-testid="job-po-requested-date">{view.expected_delivery ? fmtDate(view.expected_delivery) : "Not set"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] stamp-label text-slate-400">Expected — supplier ETA</div>
                      {["ordered", "delivered"].includes(view.status) ? (
                        <div className="mt-1">
                          <DatePicker testId="job-po-expected-date" value={view.expected_date ? view.expected_date.slice(0, 10) : ""}
                            onChange={(v) => expectedDate.mutate({ id: view.id, expected_date: v || null })} placeholder="Supplier ETA" />
                        </div>
                      ) : (
                        <div className="text-sm text-slate-400 mt-1.5">Available once the PO is sent</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-left stamp-label text-slate-500">
                      <tr><th className="px-3 py-2">Item</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Unit $</th><th className="px-3 py-2 text-right">Total</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(view.lines || []).map((ln, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-slate-800">{ln.name}</td>
                          <td className="px-3 py-2 text-right font-mono">{ln.qty} {ln.unit || ""}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtCurrencyExact(ln.unit_price || 0)}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold">{fmtCurrencyExact((ln.qty || 0) * (ln.unit_price || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="bg-slate-50"><td colSpan={3} className="px-3 py-2 text-right stamp-label text-slate-500">Total</td><td className="px-3 py-2 text-right font-mono font-bold">{fmtCurrencyExact(view.total || 0)}</td></tr></tfoot>
                  </table>
                </div>
                <div>
                  <div className="stamp-label text-slate-600 mb-2">Updates &amp; chat</div>
                  <POChat orderId={view.id} />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
